import { useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, Building2, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { WorkspaceTypeOptions } from "@/lib/utils";
import { KeyHint } from "../hints";
import { StepCard, StepCardLabel } from "../step-card";
import { chipVariants, motionSafe, staggerContainer } from "../motion";

export interface WorkspaceStepResult {
  name: string;
  type: string;
}

/**
 * Step 1 — name the workspace and say what kind of investor they are.
 *
 * The name arrives pre-filled from the org name the backend derived from their
 * email domain, so for most users this step is two clicks: confirm, pick a type.
 */
export function WorkspaceStep({
  defaultName,
  defaultType,
  onSubmit,
  isSubmitting,
}: {
  defaultName: string;
  defaultType?: string;
  onSubmit: (result: WorkspaceStepResult) => void;
  isSubmitting: boolean;
}) {
  const shouldReduce = useReducedMotion();
  const [name, setName] = useState(defaultName);
  const [type, setType] = useState<string | undefined>(
    defaultType && WorkspaceTypeOptions.includes(defaultType as never)
      ? defaultType
      : undefined
  );

  const canSubmit = name.trim().length > 0 && !!type && !isSubmitting;

  return (
    <StepCard>
      <div className="space-y-4">
        <div>
          <StepCardLabel>Workspace name</StepCardLabel>
          <div className="relative">
            <Building2 className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground/60" />
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Northwind Ventures"
              className="h-11 pl-9 text-[15px]"
              autoFocus
              onKeyDown={(e) => {
                if (e.key === "Enter" && canSubmit) onSubmit({ name: name.trim(), type: type! });
              }}
            />
          </div>
          {defaultName && name === defaultName && (
            <motion.p
              className="mt-1.5 text-xs text-muted-foreground"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3 }}
            >
              Suggested from your email — change it if you like.
            </motion.p>
          )}
        </div>

        <div>
          <StepCardLabel>What kind of investor are you?</StepCardLabel>
          <motion.div
            className="flex flex-wrap gap-2"
            variants={motionSafe(staggerContainer, shouldReduce)}
            initial="hidden"
            animate="visible"
          >
            {WorkspaceTypeOptions.map((option) => {
              const selected = type === option;
              return (
                <motion.button
                  key={option}
                  type="button"
                  variants={motionSafe(chipVariants, shouldReduce)}
                  whileHover={shouldReduce ? undefined : { y: -2 }}
                  whileTap={shouldReduce ? undefined : { scale: 0.96 }}
                  onClick={() => setType(option)}
                  className={cn(
                    "relative rounded-full border px-3.5 py-1.5 text-sm font-medium transition-colors",
                    selected
                      ? "border-primary/40 text-primary-foreground"
                      : "border-border/80 text-muted-foreground hover:border-primary/30 hover:text-foreground"
                  )}
                >
                  {/* The filled pill slides between options rather than
                      popping, which makes the choice feel continuous. */}
                  {selected && (
                    <motion.span
                      layoutId="workspace-type-pill"
                      className="absolute inset-0 rounded-full bg-primary"
                      transition={{ type: "spring", stiffness: 420, damping: 34 }}
                    />
                  )}
                  <span className="relative z-10">{option}</span>
                </motion.button>
              );
            })}
          </motion.div>
        </div>

        <div className="flex items-center justify-end gap-3 pt-1">
          <KeyHint keys={["↵"]} />
          <Button
            onClick={() => onSubmit({ name: name.trim(), type: type! })}
            disabled={!canSubmit}
            className="gap-2"
          >
            {isSubmitting ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <>
                Continue
                <ArrowRight className="h-4 w-4" />
              </>
            )}
          </Button>
        </div>
      </div>
    </StepCard>
  );
}
